import { type PointerEvent, useCallback, useRef } from 'react';

type Options = {
  onNext: () => void;
  onPrevious: () => void;
  disabled?: boolean;
  /** How far a thumb has to travel before it counts. */
  threshold?: number;
};

export type SwipeNavigation = {
  onPointerDown: (event: PointerEvent) => void;
  onPointerUp: (event: PointerEvent) => void;
  onPointerCancel: () => void;
};

/**
 * Horizontal swipe, for the viewer a thumb pages sideways.
 *
 * Pointer events rather than touch events, so a trackpad drag and a pen work
 * the same way as a thumb and there is only one code path to reason about.
 *
 * Measured on release, not followed during the gesture: the image does not
 * track the finger. That is a deliberate limit — a stack whose slides are
 * decoded one at a time cannot draw the next image in step with a drag
 * without stalling, and a half-dragged image that snaps back on a 200ms
 * decode is worse than a clean cut. The reel does not use this at all; it is
 * native CSS scroll-snap, which is the only way to get a feed that tracks the
 * finger properly.
 *
 * Vertical movement wins ties, so a scroll that drifts sideways does not
 * change image.
 */
export function useSwipeNavigation({
  onNext,
  onPrevious,
  disabled = false,
  threshold = 48,
}: Options): SwipeNavigation {
  const start = useRef<{ x: number; y: number; id: number } | null>(null);

  const onPointerDown = useCallback((event: PointerEvent) => {
    // A second finger means a pinch, which belongs to the browser.
    start.current = { x: event.clientX, y: event.clientY, id: event.pointerId };
  }, []);

  const onPointerUp = useCallback(
    (event: PointerEvent) => {
      const from = start.current;
      start.current = null;
      if (disabled || !from || from.id !== event.pointerId) return;

      const dx = event.clientX - from.x;
      const dy = event.clientY - from.y;
      if (Math.abs(dx) < threshold || Math.abs(dx) <= Math.abs(dy)) return;

      if (dx < 0) onNext();
      else onPrevious();
    },
    [disabled, threshold, onNext, onPrevious],
  );

  const onPointerCancel = useCallback(() => {
    start.current = null;
  }, []);

  return { onPointerDown, onPointerUp, onPointerCancel };
}
